import { MaskInput } from 'maska';
import '../css/global.css';
import { initA11yBar } from './a11y-bar';
import { initAnimations } from './animations';
import { initLazyVideos } from './lazy-videos';
import { initTabs } from './tabs';
import { initVideoModal } from './video-modal';

// A11y preferences restored first to prevent palette flash
initA11yBar();
// Initialize animations
initAnimations();
initTabs();
initLazyVideos();
initVideoModal();

// Smooth scroll to anchor on page load if hash exists in URL
if (window.location.hash && window.location.hash !== '#') {
	if ('scrollRestoration' in history) {
		history.scrollRestoration = 'manual';
	}
	// Scroll to top immediately to prevent default browser jump
	window.scrollTo(0, 0);

	const scrollToHash = () => {
		let target: HTMLElement | null = null;
		try {
			target = document.querySelector(
				window.location.hash,
			) as HTMLElement | null;
		} catch (err) {
			// Ignore
		}

		if (target) {
			const headerEl = document.getElementById('site-header');
			const offset = headerEl ? headerEl.getBoundingClientRect().height : 0;
			const targetRect = target.getBoundingClientRect();
			const targetTop = targetRect.top + window.scrollY - offset;

			window.scrollTo({
				top: targetTop,
				behavior: 'smooth',
			});
		}
	};

	// Try scrolling as soon as DOM is ready, and also on window load for accuracy
	window.addEventListener('DOMContentLoaded', () =>
		setTimeout(scrollToHash, 100),
	);
	window.addEventListener('load', scrollToHash);
}

// Phone Mask for inputs
const phoneMaskInputs = document.querySelectorAll('.phone-mask');
phoneMaskInputs.forEach((input) => {
	new MaskInput(input as HTMLInputElement, {
		mask: ['(##) ####-####', '(##) #####-####'],
	});
});

// Newsletter Form Handler
const newsletterForm = document.getElementById(
	'newsletter-form',
) as HTMLFormElement;
if (newsletterForm) {
	newsletterForm.addEventListener('submit', async (e) => {
		e.preventDefault();
		const messageDiv = newsletterForm.querySelector(
			'.form-message',
		) as HTMLDivElement;
		if (!messageDiv) return;

		const submitButton = newsletterForm.querySelector(
			'button[type="submit"]',
		) as HTMLButtonElement;
		const submitSpan = submitButton.querySelector('span');
		const originalText = submitSpan ? submitSpan.textContent : 'Quero receber';

		submitButton.disabled = true;
		if (submitSpan) submitSpan.textContent = 'Enviando...';

		// Reset message states
		messageDiv.classList.add('hidden');
		messageDiv.textContent = '';
		messageDiv.className =
			'form-message mt-2 hidden text-sm font-semibold rounded-2xl p-4 transition-all duration-300';

		try {
			const formData = new FormData(newsletterForm);
			const action =
				newsletterForm.getAttribute('action') ||
				(window as any).theme_ajax?.url ||
				'/wp-admin/admin-ajax.php';

			// Inject localized nonce if needed
			if (!formData.has('nonce') && (window as any).theme_ajax?.nonce) {
				formData.append('nonce', (window as any).theme_ajax.nonce);
			}

			const response = await fetch(action, {
				body: formData,
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error('Erro de conexão. Por favor, tente novamente.');
			}

			const data = await response.json();

			if (data.success) {
				messageDiv.textContent = data.data.message;
				messageDiv.className =
					'form-message mt-2 p-4 text-sm font-semibold rounded-xl bg-green-50 text-green-800 border border-green-200';
				messageDiv.classList.remove('hidden');
				newsletterForm.reset();
			} else {
				throw new Error(
					data.data.message || 'Erro ao processar sua inscrição.',
				);
			}
		} catch (error: unknown) {
			if (error instanceof Error) {
				messageDiv.textContent = error.message;
			} else {
				messageDiv.textContent =
					'Erro ao processar requisição. Tente novamente.';
			}
			messageDiv.className =
				'form-message mt-2 p-4 text-sm font-semibold rounded-xl bg-red-50 text-red-800 border border-red-200';
			messageDiv.classList.remove('hidden');
		} finally {
			submitButton.disabled = false;
			if (submitSpan) submitSpan.textContent = originalText;
		}
	});
}

// Contact Form Handler
const contactForm = document.getElementById('contact-form') as HTMLFormElement;
if (contactForm) {
	contactForm.addEventListener('submit', async (e) => {
		e.preventDefault();
		const messageDiv = contactForm.querySelector(
			'.form-message',
		) as HTMLDivElement;
		if (!messageDiv) return;

		const submitButton = contactForm.querySelector(
			'button[type="submit"]',
		) as HTMLButtonElement;
		const submitSpan = submitButton.querySelector('span');
		const originalText = submitSpan
			? submitSpan.textContent
			: 'Enviar mensagem';

		submitButton.disabled = true;
		if (submitSpan) submitSpan.textContent = 'Enviando...';

		// Reset message states
		messageDiv.classList.add('hidden');
		messageDiv.textContent = '';
		messageDiv.className =
			'form-message mt-2 hidden text-sm font-semibold rounded-2xl p-4 transition-all duration-300';

		try {
			const formData = new FormData(contactForm);
			const action =
				contactForm.getAttribute('action') ||
				(window as any).theme_ajax?.url ||
				'/wp-admin/admin-ajax.php';

			// Inject localized nonce if needed
			if (!formData.has('nonce') && (window as any).theme_ajax?.contact_nonce) {
				formData.append('nonce', (window as any).theme_ajax.contact_nonce);
			}

			const response = await fetch(action, {
				body: formData,
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error('Erro de conexão. Por favor, tente novamente.');
			}

			const data = await response.json();

			if (data.success) {
				messageDiv.textContent = data.data.message;
				messageDiv.className =
					'form-message mt-2 p-4 text-sm font-semibold rounded-xl bg-green-50 text-green-800 border border-green-200';
				messageDiv.classList.remove('hidden');
				contactForm.reset();
			} else {
				throw new Error(data.data.message || 'Erro ao enviar sua mensagem.');
			}
		} catch (error: unknown) {
			if (error instanceof Error) {
				messageDiv.textContent = error.message;
			} else {
				messageDiv.textContent =
					'Erro ao processar requisição. Tente novamente.';
			}
			messageDiv.className =
				'form-message mt-2 p-4 text-sm font-semibold rounded-xl bg-red-50 text-red-800 border border-red-200';
			messageDiv.classList.remove('hidden');
		} finally {
			submitButton.disabled = false;
			if (submitSpan) submitSpan.textContent = originalText;
		}
	});
}

// Mobile Menu Toggle
const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
const mobileMenu = document.getElementById('mobile-menu');
const body = document.body;

if (mobileMenuToggle && mobileMenu) {
	const resetMobileMenu = () => {
		mobileMenu.classList.remove('active');
		mobileMenu.classList.remove('submenu-active');
		mobileMenuToggle.classList.remove('active');
		body.classList.remove('mobile-menu-open');
		// Deactivate all submenus
		mobileMenu.querySelectorAll('.submenu-panel.active').forEach((sub) => {
			sub.classList.remove('active');
		});
	};

	mobileMenuToggle.addEventListener('click', () => {
		const isActive = mobileMenu.classList.contains('active');
		if (isActive) {
			resetMobileMenu();
		} else {
			mobileMenu.classList.add('active');
			mobileMenuToggle.classList.add('active');
			body.classList.add('mobile-menu-open');
		}
	});

	// Dynamically build mobile submenus from nested WP menus
	const mobilePanelsContainer = mobileMenu.querySelector(
		'.mobile-panels-container',
	);
	const mobileList = mobileMenu.querySelector('.mobile-menu-list');

	if (mobilePanelsContainer && mobileList) {
		const parentItems = mobileList.querySelectorAll('.menu-item-has-children');
		parentItems.forEach((item, index) => {
			const parentLi = item as HTMLElement;
			const link = parentLi.querySelector('a');
			const subMenu = parentLi.querySelector('.sub-menu');

			if (link && subMenu) {
				const submenuId = `mobile-submenu-${index}`;
				parentLi.setAttribute('data-submenu-id', submenuId);

				const parentText = link.textContent?.trim() || '';

				// Create the submenu panel element
				const panel = document.createElement('div');
				panel.id = submenuId;
				panel.className = 'submenu-panel mobile-panel';

				// Extract and format sub-menu items
				const listItems = subMenu.querySelectorAll('li');
				let listItemsHtml = '';
				listItems.forEach((li) => {
					const liLink = li.querySelector('a');
					if (liLink) {
						listItemsHtml += `
							<li class="menu-item">
								<a href="${liLink.getAttribute('href')}">${liLink.textContent}</a>
							</li>
						`;
					}
				});

				panel.innerHTML = `
					<div class="mobile-panel-header flex justify-between items-center p-6 border-b border-gray-50 mb-4">
						<button class="submenu-back-button flex items-center gap-2 text-brand-primary p-2 -mr-2" aria-label="Voltar">
							<svg class="w-6 h-6 transform rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
							</svg>
							<span class="text-lg font-bold">Voltar</span>
						</button>
						<button class="mobile-menu-close text-brand-primary p-2 -mr-2" aria-label="Fechar menu">
							<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
							</svg>
						</button>
					</div>
					<div class="p-6 pt-0">
						<h3 class="text-xl font-bold text-brand-primary mb-6">${parentText}</h3>
						<ul class="mobile-menu-list">
							${listItemsHtml}
						</ul>
					</div>
				`;

				mobilePanelsContainer.appendChild(panel);
			}
		});
	}

	// Prevent clicking parent menu items (they only open dropdowns/submenus)
	document.querySelectorAll('.menu-item-has-children > a').forEach((link) => {
		link.addEventListener('click', (e) => {
			e.preventDefault();
		});
	});

	// Close Button logic using event delegation for all panels
	mobileMenu.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;
		if (target.closest('.mobile-menu-close')) {
			resetMobileMenu();
		}
	});

	// Hierarchical Navigation (Submenus)
	const menuItemsWithSubmenu = mobileMenu.querySelectorAll(
		'.menu-item-has-children, [class*="menu-item-"]',
	);

	menuItemsWithSubmenu.forEach((item) => {
		const link = item.querySelector('a');
		const submenuId = (item as HTMLElement).dataset.submenuId;

		if (link && submenuId) {
			link.addEventListener('click', (e) => {
				// Only intercept on mobile breakpoints
				if (window.innerWidth < 1562) {
					e.preventDefault();
					e.stopPropagation();

					const submenu = document.getElementById(submenuId);
					if (submenu) {
						submenu.classList.add('active');
						mobileMenu.classList.add('submenu-active');
					}
				}
			});
		}
	});

	// Back Button Logic
	mobileMenu.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;
		const backBtn = target.closest('.submenu-back-button');

		if (backBtn) {
			const activeSubmenu = mobileMenu.querySelector('.submenu-panel.active');
			if (activeSubmenu) {
				activeSubmenu.classList.remove('active');
				mobileMenu.classList.remove('submenu-active');
			}
		}
	});

	// Close menu when clicking on a regular menu item (no submenu)
	const mobileMenuLinks = mobileMenu.querySelectorAll('.mobile-panel a');
	mobileMenuLinks.forEach((link) => {
		link.addEventListener('click', () => {
			const item = link.closest('.menu-item') as HTMLElement;
			const hasSubmenu = item?.dataset.submenuId;

			if (!hasSubmenu || window.innerWidth >= 1562) {
				resetMobileMenu();
			}
		});
	});
}

// Header Sticky & Hide on Scroll
const header = document.getElementById('site-header');
if (header) {
	let lastScrollY = window.scrollY;
	const threshold = 100; // Only hide after scrolling 100px

	const checkScroll = () => {
		const currentScrollY = window.scrollY;

		// Sticky Background
		if (currentScrollY > 0) {
			header.classList.add('is-scrolled');
		} else {
			header.classList.remove('is-scrolled');
		}

		// Hide/Show on Scroll Direction
		if (currentScrollY > lastScrollY && currentScrollY > threshold) {
			// Scrolling down
			header.classList.add('is-hidden');
		} else {
			// Scrolling up
			header.classList.remove('is-hidden');
		}

		lastScrollY = currentScrollY;
	};

	window.addEventListener('scroll', checkScroll, { passive: true });
	checkScroll(); // Check on load
}

// Scroll-to anchor handler for any link whose URL contains a hash fragment
document.addEventListener('click', (e) => {
	const link = (e.target as HTMLElement).closest(
		'a',
	) as HTMLAnchorElement | null;
	if (!link) return;

	// Use link.hash to get the hash fragment (e.g. "#conteudo")
	const hash = link.hash;
	if (!hash || hash === '#') return;

	// Normalize paths to compare if the link targets the current page
	const normalizePath = (path: string) => path.replace(/\/$/, '');
	const isSamePage =
		link.origin === window.location.origin &&
		normalizePath(link.pathname) === normalizePath(window.location.pathname) &&
		link.search === window.location.search;

	if (!isSamePage) {
		// Let browser handle navigation to another page; the page load smooth-scroll handler will scroll there.
		return;
	}

	let target: HTMLElement | null = null;
	try {
		target = document.querySelector(hash) as HTMLElement | null;
	} catch (err) {
		// Ignore invalid selector exceptions and let the browser handle it
	}

	if (!target) return;

	e.preventDefault();

	// Close mobile menu if open
	const mobileMenuEl = document.getElementById('mobile-menu');
	if (mobileMenuEl?.classList.contains('active')) {
		mobileMenuEl.classList.remove('active', 'submenu-active');
		document.getElementById('mobile-menu-toggle')?.classList.remove('active');
		document.body.classList.remove('mobile-menu-open');
	}

	const targetRect = target.getBoundingClientRect();
	const isScrollingUp = targetRect.top < 0;

	let offset = 0;
	if (isScrollingUp) {
		const headerEl = document.getElementById('site-header');
		offset = headerEl ? headerEl.getBoundingClientRect().height : 0;
	}

	const targetTop = targetRect.top + window.scrollY - offset;

	window.scrollTo({ top: targetTop, behavior: 'smooth' });
});
